import { api } from './api';
import type {
  ClientDocument,
  SiteDocument,
  SiteDocumentUpdate,
  SiteDocumentUrlResponse,
  PaginatedResponse,
  DocumentType,
} from '../types/api';

type DocumentFilters = {
  document_type?: DocumentType;
  // 'inspector' = BMP maps + site notices only, filtered server-side (a site
  // can hold thousands of inspection reports; paging through them is not an
  // option on a phone).
  scope?: 'inspector';
  page?: number;
  size?: number;
};

// Every DocumentType's label. The plain NOI / NOC / NOT / Permit / Notice types stay for files
// already typed that way but are no longer offered: Jim (2026-10-16) wants each form to say
// whose it is (Owner or GC), and the lists in alphabetical order.
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  bmp_map: 'BMP Map',
  compliance_report: 'Compliance Report',
  contract: 'Contract',
  correspondence: 'Correspondence',
  drawing: 'Drawing',
  gc_certification: 'GC Certification',
  gc_delegation: 'GC Delegation',
  gc_noc: 'GC NOC',
  gc_noi: 'GC NOI',
  gc_not: 'GC NOT',
  gc_permit: 'GC Permit',
  gc_site_notice: 'GC Site Notice',
  inspection_report: 'Inspection Report',
  invoice: 'Invoice',
  noi: 'NOI (Notice of Intent)',
  notice: 'Notice',
  notice_of_change: 'Notice of Change',
  notice_of_termination: 'Notice of Termination',
  other: 'Other',
  owner_certification: 'Owner Certification',
  owner_delegation: 'Owner Delegation',
  owner_noc: 'Owner NOC',
  owner_noi: 'Owner NOI',
  owner_not: 'Owner NOT',
  owner_permit: 'Owner Permit',
  owner_site_notice: 'Owner Site Notice',
  permit: 'Permit',
  photo: 'Photo',
  proposal: 'Proposal',
  swppp: 'SWPPP',
};

export const RETIRED_DOCUMENT_TYPES: ReadonlySet<DocumentType> =
  new Set<DocumentType>([
    'noi',
    'notice_of_termination',
    'notice_of_change',
    'permit',
    'notice',
  ]);

/** The types offered in every picker, alphabetical by label. */
export const DOCUMENT_TYPE_VALUES: DocumentType[] = (
  Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]
)
  .filter((t) => !RETIRED_DOCUMENT_TYPES.has(t))
  .sort((a, b) =>
    DOCUMENT_TYPE_LABELS[a].localeCompare(DOCUMENT_TYPE_LABELS[b])
  );

type UploadDocumentParams = {
  file: File;
  title: string;
  description?: string;
  document_type?: DocumentType;
  tags?: string[];
};

class DocumentsService {
  async getSiteDocuments(
    siteId: string,
    filters: DocumentFilters = {}
  ): Promise<PaginatedResponse<SiteDocument>> {
    const params = new URLSearchParams();

    if (filters.document_type) {
      params.append('document_type', filters.document_type);
    }
    if (filters.scope) {
      params.append('scope', filters.scope);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.size) {
      params.append('size', filters.size.toString());
    }

    const response = await api.get(
      `/sites/${siteId}/documents?${params.toString()}`
    );
    return response.data as PaginatedResponse<SiteDocument>;
  }

  async getClientDocuments(
    clientId: string,
    filters: DocumentFilters = {}
  ): Promise<PaginatedResponse<ClientDocument>> {
    const params = new URLSearchParams();

    if (filters.document_type) {
      params.append('document_type', filters.document_type);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.size) {
      params.append('size', filters.size.toString());
    }

    const response = await api.get(
      `/clients/${clientId}/documents?${params.toString()}`
    );
    return response.data as PaginatedResponse<ClientDocument>;
  }

  async getDocument(documentId: string): Promise<SiteDocument> {
    const response = await api.get(`/documents/${documentId}`);
    return response.data as SiteDocument;
  }

  async uploadDocument(
    siteId: string,
    params: UploadDocumentParams
  ): Promise<SiteDocument> {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('title', params.title);

    if (params.description) {
      formData.append('description', params.description);
    }
    if (params.document_type) {
      formData.append('document_type', params.document_type);
    }
    if (params.tags && params.tags.length > 0) {
      formData.append('tags', JSON.stringify(params.tags));
    }

    const response = await api.post(`/sites/${siteId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data as SiteDocument;
  }

  async updateDocument(
    documentId: string,
    data: SiteDocumentUpdate
  ): Promise<SiteDocument> {
    const response = await api.patch(`/documents/${documentId}`, data);
    return response.data as SiteDocument;
  }

  async deleteDocument(documentId: string): Promise<void> {
    await api.delete(`/documents/${documentId}`);
  }

  /**
   * JIM-1007-01: the document's bytes through the API (the bucket has no CORS
   * for the browser), for drawing a PDF page by page on a phone.
   */
  async fetchDocumentBytes(documentId: string): Promise<Uint8Array> {
    const r = await api.get<ArrayBuffer>(`/documents/${documentId}/file`, {
      responseType: 'arraybuffer',
    });
    return new Uint8Array(r.data);
  }

  async getDocumentUrl(
    documentId: string,
    download = false
  ): Promise<SiteDocumentUrlResponse> {
    const params = new URLSearchParams();
    if (download) {
      params.append('download', 'true');
    }
    const response = await api.get(
      `/documents/${documentId}/url?${params.toString()}`
    );
    return response.data as SiteDocumentUrlResponse;
  }

  // Utility methods
  getDocumentTypeOptions() {
    return DOCUMENT_TYPE_VALUES.map((value) => ({
      value,
      label: DOCUMENT_TYPE_LABELS[value],
    }));
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getDocumentTypeLabel(type: DocumentType): string {
    return DOCUMENT_TYPE_LABELS[type] || type;
  }

  isPDF(mimeType: string): boolean {
    return mimeType === 'application/pdf';
  }
}

export const documentsService = new DocumentsService();
